import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useDeliveryMode } from '@/contexts/DeliveryModeContext';
import { useAuth } from '@/AuthContext';

// Define allowed routes when delivery mode is active
const ALLOWED_DELIVERY_ROUTES = [
  '/rider',
  '/rider/job-claim',
  '/rider/my-jobs',
  '/rider/wallet',
  '/rider/analytics',
  '/rider/dashboard',
  '/rider/settings',
  '/rider/profile',
  '/rider/help',
  '/rider/support'
];

// Helper function to check if current route is allowed in delivery mode
const isDeliveryRoute = (pathname) => {
  return ALLOWED_DELIVERY_ROUTES.some(route => pathname.startsWith(route));
};

const DeliveryModeRouteGuard = ({ children }) => {
  const { isDeliveryMode, isLoading } = useDeliveryMode();
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();

  // Wait for both auth and delivery mode to load
  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If user is not authenticated, let other auth guards handle it
  if (!user) {
    return children;
  }

  // If delivery mode is not active, render children normally
  if (!isDeliveryMode) {
    return children;
  }

  // If delivery mode is active, check if current route is allowed
  // Special-case: allow home page '/' so users can always access Home
  if (location.pathname === '/') {
    return children;
  }
  if (isDeliveryRoute(location.pathname)) {
    return children;
  }

  // If delivery mode is active and route is not allowed, redirect to rider job claim
  // Only redirect if we're not already on a rider page to prevent loops
  if (!location.pathname.startsWith('/rider')) {
    return <Navigate to="/rider/job-claim" replace />;
  }

  return children;
};

export default DeliveryModeRouteGuard;
